import React, { useEffect, useState } from "react";
import { Card, Descriptions, Spin, message, Tag, Space, Image } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { Content } from "@/types";
import { apiService } from "@/services/api";
import { useProcessingStatus } from "@/hooks/useProcessingStatus";
import { ProcessingStatusBadge } from "@/components/media/ProcessingStatusBadge";

export const ContentShow: React.FC = () => {
  const { id } = useParams();
  const [content, setContent] = useState<Content | null>(null);
  const [loading, setLoading] = useState(true);

  const { data: processingStatus } = useProcessingStatus(
    content?.id ?? null,
    true
  );

  useEffect(() => {
    (async () => {
      try {
        const data = await apiService.getContent(Number(id));
        setContent(data);
      } catch (e) {
        message.error("Impossible de charger le contenu");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Spin />;
  if (!content) return null;

  // Use live processing status if available, fallback to content data
  const videoStatus = processingStatus?.videoProcessingStatus ?? content.videoProcessingStatus;
  const videoProgress = processingStatus?.videoProcessingProgress ?? null;
  const videoStage = processingStatus?.videoProcessingStage ?? null;
  const videoError =
    processingStatus?.videoProcessingError ?? content.videoProcessingError ?? null;
  const trailerStatus = processingStatus?.trailerProcessingStatus ?? content.trailerProcessingStatus;
  const trailerProgress = processingStatus?.trailerProcessingProgress ?? null;
  const trailerStage = processingStatus?.trailerProcessingStage ?? null;
  const trailerError =
    processingStatus?.trailerProcessingError ?? content.trailerProcessingError ?? null;

  return (
    <div>
      <Card title={`${content.title}`} style={{ marginBottom: 16 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Titre" span={2}>
            {content.title}
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            {content.type?.title || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            <Tag color={content.status === 'PUBLISHED' ? 'green' : 'orange'}>
              {content.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Date de publication">
            {content.release_date ? new Date(content.release_date).toLocaleDateString('fr-FR') : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Langue originale">
            {content.originalLanguage?.toUpperCase() || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {content.overview || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Genres" span={2}>
            {content.genres?.map((g: any) => (
              <Tag key={g.id} color="blue">{g.title}</Tag>
            )) || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Tags" span={2}>
            {content.tags?.map((tag: string, idx: number) => (
              <Tag key={idx}>{tag}</Tag>
            )) || 'Aucun tag'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Informations specifiques au Film */}
      {content.movie && (
        <Card title="Informations Film" style={{ marginBottom: 16 }}>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Duree">
              {content.movie.duration} minutes
            </Descriptions.Item>
            <Descriptions.Item label="Realisateur">
              {content.movie.director || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Budget">
              {content.movie.budget ? `${content.movie.budget.toLocaleString('fr-FR')} EUR` : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Box Office">
              {content.movie.boxOffice ? `${content.movie.boxOffice.toLocaleString('fr-FR')} EUR` : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Informations specifiques a la Serie */}
      {content.series && (
        <Card title="Informations Serie" style={{ marginBottom: 16 }}>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Nombre de saisons">
              {content.series.totalSeasons || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Nombre d'episodes">
              {content.series.totalEpisodes || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Duree moyenne des episodes" span={2}>
              {content.series.averageEpisodeDuration ? `${content.series.averageEpisodeDuration} minutes` : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Medias et Statut de Transcodage */}
      <Card title="Medias" style={{ marginBottom: 16 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Affiche" span={2}>
            {content.poster_path ? (
              <Space direction="vertical">
                <Tag color="success" icon={<CheckCircleOutlined />}>Uploadee</Tag>
                <Image
                  src={content.poster_path}
                  alt="Affiche"
                  width={120}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFoiAAAAABJRU5ErkJggg=="
                />
              </Space>
            ) : (
              <Tag color="warning" icon={<CloseCircleOutlined />}>Non disponible</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Bande d'annonce" span={1}>
            {content.trailerUrl ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>Uploadee</Tag>
            ) : (
              <Tag color="default">Non uploadee</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Statut trailer" span={1}>
            <ProcessingStatusBadge
              status={trailerStatus || null}
              progress={trailerProgress}
              stage={trailerStage}
              error={trailerError}
              type="trailer"
            />
          </Descriptions.Item>
          <Descriptions.Item label="Video complete" span={1}>
            {content.hlsManifestUrl ? (
              <Tag color="success" icon={<CheckCircleOutlined />}>Disponible</Tag>
            ) : (
              <Tag color="default">Non uploadee</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Statut video" span={1}>
            <ProcessingStatusBadge
              status={videoStatus || null}
              progress={videoProgress}
              stage={videoStage}
              error={videoError}
              type="video"
            />
          </Descriptions.Item>
          <Descriptions.Item label="Resolution">
            {content.videoResolution || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Qualite">
            {content.videoQuality || 'N/A'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default ContentShow;
